export type PdfSanitizeOptions = {
  allowedSchemes: string[];
  allowedTags: string[];
  allowedAttributes: Record<string, string[]>;
  allowVulnerableTags: boolean;
};

export const PDF_SANITIZE_OPTIONS: PdfSanitizeOptions = {
  allowedSchemes: ['http', 'https', 'mailto', 'tel', 'data'],
  allowedTags: [
    'address',
    'article',
    'aside',
    'footer',
    'header',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hgroup',
    'html',
    'nav',
    'section',
    'page',
    'body',
    'title',
    'svg',
    'blockquote',
    'dd',
    'div',
    'dl',
    'dt',
    'figcaption',
    'figure',
    'hr',
    'li',
    'main',
    'ol',
    'p',
    'pre',
    'ul',
    'a',
    'abbr',
    'b',
    'bdi',
    'bdo',
    'br',
    'cite',
    'code',
    'data',
    'dfn',
    'em',
    'i',
    'kbd',
    'mark',
    'q',
    'rb',
    'rp',
    'rt',
    'rtc',
    'ruby',
    's',
    'samp',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'time',
    'u',
    'var',
    'wbr',
    'caption',
    'col',
    'colgroup',
    'table',
    'tbody',
    'td',
    'tfoot',
    'th',
    'thead',
    'meta',
    'tr',
    'style',
    'iframe',
    'path',
    'defs',
    'lineargradient',
    'stop',
    'picture',
    'img',
    'g',
    'circle',
    'line',
    'rect',
    'text',
    'filter',
    'feFlood',
    'feflood',
    'feColorMatrix',
    'fecolormatrix',
    'feOffset',
    'feoffset',
    'feGaussianBlur',
    'fegaussianblur',
    'feComposite',
    'fecomposite',
    'feBlend',
    'feblend',
  ],
  allowedAttributes: {
    a: ['rel', 'href', 'name', 'target', 'class'],
    h1: ['id', 'class'],
    h2: ['id', 'class'],
    h3: ['id', 'class'],
    h4: ['id', 'class'],
    h5: ['id', 'class'],
    h6: ['id', 'class'],
    hr: ['class'],
    em: ['class'],
    p: ['class'],
    ul: ['class'],
    li: ['class'],
    // for graphs
    text: [
      'x',
      'dx',
      'y',
      'dy',
      'fill',
      'font-size',
      'font-weight',
      'text-anchor',
      'style',
    ],
    circle: ['cx', 'cy', 'r', 'fill', 'stroke', 'stroke-width'],
    rect: ['x', 'y', 'width', 'height', 'fill', 'transform', 'rx', 'data-ref'],
    line: [
      'x1',
      'x2',
      'y1',
      'y2',
      'fill',
      'stroke',
      'stroke-width',
      'stroke-dasharray',
      'stroke-linecap',
    ],
    g: [
      'transform',
      'opacity',
      'text-anchor',
      'font-size',
      'filter',
      'data-ref',
    ],
    filter: [
      'id',
      'x',
      'y',
      'width',
      'height',
      'filterUnits',
      'filterunits',
      'color-interpolation-filters',
      'data-ref',
      'data-id',
    ],
    feFlood: ['flood-opacity', 'result', 'data-ref'],
    feflood: ['flood-opacity', 'result', 'data-ref'],
    feColorMatrix: ['in', 'type', 'values', 'result', 'data-ref'],
    fecolormatrix: ['in', 'type', 'values', 'result', 'data-ref'],
    feGaussianBlur: ['stdDeviation', 'stddeviation', 'data-ref'],
    fegaussianblur: ['stdDeviation', 'stddeviation', 'data-ref'],
    feComposite: ['in2', 'operator', 'data-ref'],
    fecomposite: ['in2', 'operator', 'data-ref'],
    feBlend: ['mode', 'in', 'in2', 'result', 'data-ref'],
    feblend: ['mode', 'in', 'in2', 'result', 'data-ref'],
    feOffset: ['dx', 'dy', 'in', 'result', 'data-ref'],
    feoffset: ['dx', 'dy', 'in', 'result', 'data-ref'],
    html: ['lang'],
    header: ['class', 'style'],
    section: ['class'],
    head: ['class'],
    article: ['class'],
    meta: ['name', 'http-equiv', 'content'],
    path: [
      'd',
      'fill',
      'stroke',
      'stroke-width',
      'fill-rule',
      'clip-rule',
      'opacity',
      'fill-opacity',
      'data-ref',
    ],
    span: ['hidden', 'class'],
    stop: ['stop-color', 'offset', 'fill', 'stop-opacity', 'class'],
    svg: [
      'class',
      'viewbox',
      'fill',
      'preserveaspectratio',
      'width',
      'height',
      'xmlns',
      'style',
      'data-ref',
      'aria-hidden',
    ],
    defs: ['data-ref'],
    picture: ['class'],
    img: ['class', 'src', 'alt', 'onerror'],
    lineargradient: ['x1', 'y1', 'x2', 'y2', 'id', 'gradientunits'],
    page: ['class', 'style', 'size', 'id', 'hidden'],
    main: ['class', 'style', 'hidden'],
    footer: ['class', 'style'],
    div: ['class', 'style', 'hidden', 'id'],
    col: ['style', 'span'],
    colgroup: ['style', 'span'],
    table: ['class', 'style'],
    td: ['class', 'style', 'colspan', 'rowspan'],
    tr: ['class', 'style', 'colspan', 'rowspan'],
    th: ['class', 'style', 'colspan', 'rowspan'],
    body: ['class', 'style'],
    tfoot: ['class', 'colspan', 'rowspan'],
    tbody: ['class', 'colspan', 'rowspan'],
    thead: ['class', 'colspan', 'rowspan'],
    iframe: ['src', 'width', 'height'],
  },
  allowVulnerableTags: true,
};

const PDF_TAGS = new Set(
  PDF_SANITIZE_OPTIONS.allowedTags.map(tag => tag.toLowerCase()),
);

export const pdfKeptAttributes = (tag: string): string[] =>
  PDF_SANITIZE_OPTIONS.allowedAttributes[tag.toLowerCase()] ?? [];

export const pdfKeepsTag = (tag: string): boolean =>
  PDF_TAGS.has(tag.toLowerCase());

export const pdfKeepsAttribute = (tag: string, attribute: string): boolean =>
  pdfKeptAttributes(tag).includes(attribute.toLowerCase());
